import InvestorStatistics from '@components/SuburbProfile/InvestorStatistics'
import MedianPrice from '@components/SuburbProfile/MedianPrice'
import NearbySuburbs from '@components/SuburbProfile/NearbySuburbs'
import SalesComparison from '@components/SuburbProfile/SalesComparison'
import SaleOverview from '@components/SuburbProfile/SalesOverview'
import ServicedByTitles from '@components/SuburbProfile/ServicedBy/ServicedByTitles'
import SuburbInfo from '@components/SuburbProfile/SuburbInfo'
import SuburbProfileHero from '@components/SuburbProfile/SuburbProfileHero'
import SuburbNote from '@components/SuburbProfile/SuburbNote'
import dynamic from 'next/dynamic'
import CTA from '@global/Blocks/CTA'
import NewsList from '@global/Blocks/NewsList'
import Breadcrumbs from '@global/Breadcrumbs'
import Spacer from '@global/Spacer'
import StackedBarChart from '@global/StackedBarChart'
import { getChildPages } from '@lib/api-services/cmsService'
import { getCanonicalURL } from '@lib/api'
import { defaultImages } from '@lib/variables'
import { Container, Section } from '@styles/global.styled'
import { NextSeo } from 'next-seo'
import WagtailBlocksSection from '../components/global/Blocks/WagtailBlocksSection'
import SuburbSEOLinks from '../components/global/SuburbSEOLinks'

const SuburbProfilePdfDownloadButton = dynamic(
  () => import('@components/SuburbProfileReportPdf/DownloadButton'),
  { ssr: false }
)

const SuburbPage = ({ pageData }) => {
  const {
    title,
    content_blocks,
    state,
    postcode,
    preamble,
    featured_image,
    meta,
    article,
    investor_stats,
    local_education,
    sales_comparison,
    five_year_growth,
    median_prices,
    property_stats,
    property_types_breakdown,
    occupancy_stats,
    nearby_suburbs,
    cta_block,
    auto_news_insights,
    sales_overview,
    data_disclaimer,
    available_bedroom_counts,
  } = pageData || {}
  const defaultTitle = `${title} Property Market & Suburb Profile`
  const defaultDescription = `${title}, ${postcode} Property Market Report: Your essential guide to median house prices, sales results, rental yields, and local demographic information.`
  const canonicalURL = getCanonicalURL({ canonical: meta?.canonical, path: meta?.path })
  const metaTitle = meta.meta_title || defaultTitle
  const metaDescription = meta.meta_description || defaultDescription || ''
  const ogTitle = meta.og_title || metaTitle
  const ogImage = meta.og_img
    ? [{ url: meta.og_img }]
    : featured_image?.url
    ? [{ url: featured_image?.url }]
    : [{ url: defaultImages.mediumNavy }]
  const ogDescription =
    meta.og_description ||
    `Discover everything you need to know about the ${title} property market, including median prices, sales data, investor insights, and local demographics.`

  const keyServicedOfficeBlocks = content_blocks.filter(
    (it) => it.type === 'key_serviced_office' && it.value?.path
  )

  const servicingOffices = content_blocks.find(
    (it) => it.type === 'servicing_offices'
  )

  const restBlocks = content_blocks.filter(
    (it) => it.type !== 'key_serviced_office' && it.type !== 'servicing_offices'
  )

  const isShowOccupancyOwnedRented = !!(
    occupancy_stats?.owned_pct || occupancy_stats?.rented_pct
  )
  const isShowPropertyTypesBreakdown = property_types_breakdown?.items?.find(
    (it) => it.percent > 0
  )

  const isShowOccupancySinglesFamilies = !!(
    occupancy_stats?.singles_pct || occupancy_stats?.families_pct
  )

  return (
    <div className='suburb-profile-page'>
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalURL}
        openGraph={{
          images: ogImage,
          title: ogTitle,
          description: ogDescription,
          url: canonicalURL,
          type: 'website',
        }}
      />

      <SuburbProfileHero
        breadcrumbs={
          <Breadcrumbs
            breadcrumbs={meta.breadcrumbs?.map((it) => ({
              ...it,
              label: it.title,
            }))}
          />
        }
        title={meta?.parent?.title}
        subTitle={<span>{title}</span>}
        h1={
          <>
            {title} Property Market
            <br /> & Suburb Profile
          </>
        }
        image={featured_image?.url}
        pdfDownload={<SuburbProfilePdfDownloadButton pageData={pageData} fileSlug={meta?.slug} />}
      />
      {Object.keys(median_prices || {}).length > 0 && (
        <MedianPrice
          suburb={title.toUpperCase()}
          medianPrices={median_prices}
          slug={meta.slug}
          availableBedrooms={available_bedroom_counts}
        />
      )}

      {Object.keys(sales_overview || {}).length > 0 && (
        <SaleOverview
          suburb={title}
          salesOverview={sales_overview}
        />
      )}

      {investor_stats && (
        <InvestorStatistics
          suburb={title}
          investorStats={investor_stats}
          slug={meta.slug}
          availableBedrooms={available_bedroom_counts}
        />
      )}

      {isShowPropertyTypesBreakdown ||
      isShowOccupancyOwnedRented ||
      isShowOccupancySinglesFamilies ? (
        <Section withPaddingBottom>
          <Container>
            {isShowPropertyTypesBreakdown ? (
              <>
                <StackedBarChart
                  title={`${title.toUpperCase()} PROPERTY TYPES`}
                  data={property_types_breakdown?.items
                    ?.map((it) => ({
                      label: it.label,
                      value: it.percent,
                    }))
                    .filter((item) => item.value > 0)}
                />
                {isShowOccupancyOwnedRented && <Spacer height={30} />}
              </>
            ) : null}

            {isShowOccupancyOwnedRented ? (
              <>
                <StackedBarChart
                  title={`OCCUPANCY IN ${title.toUpperCase()}`}
                  data={[
                    { label: 'Owned', value: occupancy_stats?.owned_pct },
                    { label: 'Rented', value: occupancy_stats?.rented_pct },
                    {
                      label: 'Other/Not Stated',
                      value:
                        100 -
                        (occupancy_stats?.owned_pct || 0) -
                        (occupancy_stats?.rented_pct || 0),
                    },
                  ]}
                />
                {isShowOccupancySinglesFamilies && <Spacer height={22} />}
              </>
            ) : null}

            {isShowOccupancySinglesFamilies ? (
              <StackedBarChart
                data={[
                  { label: 'Singles', value: occupancy_stats.singles_pct },
                  { label: 'Families', value: occupancy_stats.families_pct },
                  { label: 'Other', value: occupancy_stats.group_pct },
                ]}
              />
            ) : null}
          </Container>
        </Section>
      ) : null}

      {cta_block?.headline && (
        <CTA
          cta={{
            title: cta_block.headline,
            content: cta_block.content_html,
            buttonUrl: cta_block.button_url,
            buttonText: cta_block.button_text,
            image: cta_block.cta_image,
            imagePosition: cta_block.image_placement,
          }}
        />
      )}

      {(preamble || article?.length > 0 || local_education?.length > 0) && (
        <SuburbInfo
          suburb={title}
          preamble={preamble}
          article={article?.filter((it) => it.type === 'paragraph')}
          localEducation={local_education || []}
        />
      )}

      {(five_year_growth?.length > 0 ||
        sales_comparison?.house?.length > 0 ||
        sales_comparison?.unit?.length > 0) && (
        <SalesComparison
          suburb={title}
          fiveYearGrowth={five_year_growth}
          salesComparison={sales_comparison}
          slug={meta.slug}
          availableBedrooms={available_bedroom_counts}
        />
      )}

      <WagtailBlocksSection blocks={restBlocks} />

      {keyServicedOfficeBlocks.length > 0 && (
        <div className='key-service-office'>
          <Section>
            <Container>
              <h2 className='key-service-office-title'>
                PROUDLY SERVICING {title}
              </h2>
            </Container>
            <WagtailBlocksSection blocks={keyServicedOfficeBlocks} />
          </Section>
        </div>
      )}

      {servicingOffices && (
        <ServicedByTitles
          {...ServicedByTitles.ApiBlockToProps(servicingOffices)}
          title={`PROUDLY SERVICING ${title.toUpperCase()}`}
        />
      )}

      {nearby_suburbs?.nearby_suburbs?.length > 0 && (
        <NearbySuburbs
          title='BROWSE MORE SUBURBS'
          nearbySuburbs={nearby_suburbs.nearby_suburbs.map((it) => ({
            ...it,
            image: it.featured_image,
          }))}
          viewMoreLink={{
            text: nearby_suburbs.view_more_text || 'View more',
            url: nearby_suburbs.region_page_url,
            position: 'bottom',
            hideIcon: true,
          }}
        />
      )}

      <SuburbSEOLinks
        suburb={title}
        state={state}
        propertyStats={property_stats}
        slug={meta.slug}
        bgColor='white'
        noPaddingTop
      />

      {auto_news_insights?.latest_news?.length > 0 && (
        <Section noPaddingTop withPaddingBottom>
          <NewsList
            title={auto_news_insights.title}
            articles={auto_news_insights.latest_news?.map((it) => ({
              title: it.title,
              imgUrl: it.header_image_results?.url,
              date: it.date,
              link: {
                url: it.path,
                label: 'read article',
              },
            }))}
            number_of_rows={1}
            link={{
              url:
                auto_news_insights.cta_external_url ||
                auto_news_insights.cta_url,
              text: auto_news_insights.cta_text,
            }}
          />
        </Section>
      )}

      <SuburbNote disclaimer={data_disclaimer} />
    </div>
  )
}

SuburbPage.getInitialProps = async (ctx, pageData) => {
  let isSuburbPage = false
  if (ctx && ctx.query && ctx.query.slug) {
    isSuburbPage = ctx.query.slug.includes('suburb-profile')
  }
  const { id, lifestyle_categories, property_stats } = pageData
  let sidebar = []
  let childrenPages = await getChildPages('suburbs.SuburbPage', id, 1)

  if (lifestyle_categories) {
    sidebar.push({
      title: 'Lifestyle',
      listItems: lifestyle_categories.map((item) => item.category.title),
    })
  }

  if (property_stats) {
    sidebar = sidebar.concat([
      {
        title: 'Properties for Sale with Barry Plant',
        text: property_stats.for_sale,
        link: `/for-sale/properties-sale/?address_suburb=${pageData.title}`,
        type: 'sale',
      },
    ])

    if (property_stats.sold > 0) {
      sidebar = sidebar.concat([
        {
          title: 'Properties Sold by Barry Plant (since 2016)',
          text: property_stats.sold,
          link: `/sold-properties/?address_suburb=${pageData.title}`,
          type: 'sold',
        },
      ])
    }

    sidebar = sidebar.concat([
      {
        title: 'Properties for Rent with Barry Plant',
        text: property_stats.for_rent,
        link: `/rental-properties/properties-rent/?address_suburb=${pageData.title}`,
        type: 'rent',
      },
    ])

    if (property_stats.leased > 0) {
      sidebar = sidebar.concat([
        {
          title: 'Properties Leased by Barry Plant (since 2016)',
          text: property_stats.leased,
          link: `/rental-properties/properties-rent/?address_suburb=${pageData.title}&status=leased`,
          type: 'leased',
        },
      ])
    }
  }

  return {
    region: {
      title: 'Suburb profile by region',
      blocks: childrenPages.items.map((item) => {
        return {
          copy: item.description,
          title: item.title,
          image: item.featured_image_thumb && item.featured_image_thumb.url,
          link: item.meta.path,
        }
      }),
    },
    sidebar: sidebar,
    isSuburbPage: isSuburbPage,
    pageData,
  }
}
export default SuburbPage
